import Modal from "@/components/Modal/Modal";
import { List, ListItem, ListItemButton, ListItemText, Radio, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

interface Props<T> {
    label: string;
    required?: boolean;
    error?: boolean;
    helperText?: string | undefined;
    heading: string;
    saveButtonText?: string;
    list: T[];
    idKey: keyof T;
    nameKey: keyof T;
    displayKey: keyof T;
    defaultValue: string;
    setOption: (value: any | null) => void;
    setValueChanged?: (value: boolean) => void;
}

const RadioAssign = <T extends Record<string, any>>({
    label,
    required = false,
    error = false,
    helperText,
    heading,
    saveButtonText = "Done",
    list,
    idKey,
    nameKey,
    displayKey,
    defaultValue,
    setOption,
    setValueChanged,
}: Props<T>) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [displayValue, setDisplayValue] = useState("");
    const handleToggle = (Id: number) => {
        setSelectedId(Id)
    };
    const [modalOpen, setModalOpen] = useState(false);
    const filteredList = useMemo(() => {
        const search = searchValue.trim().toLowerCase();
        if (!search) return list;

        return list.filter((item) =>
            String(item?.[nameKey] ?? "")
                .toLowerCase()
                .includes(search)
        );
    }, [list, searchValue, nameKey]);
    const onSubmit = () => {
        setValueChanged?.(true);
        const option = list.find(
            (x) => x[idKey] === selectedId
        )
        setOption(option || null);
        setDisplayValue(
            option?.[displayKey] != null
                ? String(option[displayKey])
                : displayValue
        );
        setModalOpen(false);
    }
    useEffect(() => {
        if (defaultValue)
            setDisplayValue(defaultValue);
    }, [defaultValue])

    return <>
        <TextField
            label={label}
            variant="standard"
            fullWidth
            value={displayValue}
            onClick={() => setModalOpen(true)}
            required={required}
            error={error}
            helperText={helperText}
            slotProps={{ input: { readOnly: true } }}
        />
        <Modal
            modalOpen={modalOpen}
            closeModal={() => { setModalOpen(false) }}
            heading={heading}
            width="450px"
            hideFooter={false}
            saveButtonText={saveButtonText}
            onSave={onSubmit}
            isDisabled={selectedId === null}
        >
            <TextField
                variant="standard"
                placeholder="Search"
                fullWidth
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{ mb: 2 }}
            />
            <List>
                {filteredList?.length > 0 && filteredList?.map((option: any) => (
                    <ListItem key={option?.[idKey]} disablePadding>
                        <ListItemButton onClick={() => handleToggle(option?.[idKey])}>
                            <ListItemText primary={option?.[nameKey]}
                                slotProps={{
                                    primary: {
                                        variant: "body1",
                                        fontSize: 13,
                                        fontWeight: 500
                                    }
                                }} />
                            <Radio
                                edge="end"
                                checked={selectedId === option?.[idKey]}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
                {filteredList?.length === 0 &&
                    <Typography variant="body2" align="center">No records available</Typography>
                }
            </List>
        </Modal>
    </>
}

export default RadioAssign;